import React from "react";
import colors from "../theme/colors";
import radius from "../theme/radius";
import typography from "../theme/typography";
import shadows from "../theme/shadows";

const sizeStyles = {
  sm: {
    minHeight: 40,
    padding: "0 36px 0 12px",
    fontSize: typography.size.sm,
  },
  md: {
    minHeight: 46,
    padding: "0 40px 0 14px",
    fontSize: typography.size.sm,
  },
  lg: {
    minHeight: 52,
    padding: "0 44px 0 16px",
    fontSize: typography.size.base,
  },
};

const variantStyles = {
  default: {
    background: colors.surface,
    backgroundDisabled: colors.surfaceSoft,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: colors.textMuted,
    icon: colors.textMuted,
  },
  soft: {
    background: colors.surfaceSoft,
    backgroundDisabled: colors.surfaceSoft,
    border: colors.borderSoft || colors.border,
    text: colors.textPrimary,
    placeholder: colors.textMuted,
    icon: colors.textMuted,
  },
};

function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="m6 9 6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Select({
  children,
  value,
  onChange,
  options = [],
  placeholder = "Selecione",
  size = "md",
  variant = "default",
  disabled = false,
  invalid = false,
  fullWidth = true,
  style = {},
  className = "",
  name,
  id,
  ...rest
}) {
  const resolvedSize = sizeStyles[size] || sizeStyles.md;
  const resolvedVariant = variantStyles[variant] || variantStyles.default;

  const hasPlaceholder =
    placeholder !== null && placeholder !== undefined;

  const isEmpty =
    value === "" || value === null || value === undefined;

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: fullWidth ? "100%" : "auto",
      }}
    >
      <select
        id={id}
        name={name}
        value={isEmpty ? "" : value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        style={{
          width: "100%",
          minHeight: resolvedSize.minHeight,
          borderRadius: radius.sm,
          border: `1.5px solid ${
            invalid ? colors.error : resolvedVariant.border
          }`,
          background: disabled
            ? resolvedVariant.backgroundDisabled
            : resolvedVariant.background,
          padding: resolvedSize.padding,
          fontFamily: typography.fontFamily,
          fontSize: resolvedSize.fontSize,
          fontWeight: typography.weight.medium,
          color: isEmpty
            ? resolvedVariant.placeholder
            : resolvedVariant.text,
          outline: "none",
          boxSizing: "border-box",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          transition:
            "border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease",
          opacity: disabled ? 0.7 : 1,
          WebkitTapHighlightColor: "transparent",
          ...style,
        }}
        onFocus={(event) => {
          event.currentTarget.style.boxShadow = shadows.sm;
          if (rest.onFocus) rest.onFocus(event);
        }}
        onBlur={(event) => {
          event.currentTarget.style.boxShadow = "none";
          if (rest.onBlur) rest.onBlur(event);
        }}
        {...rest}
      >
        {hasPlaceholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}

        {options.map((option) => {
          const isObject =
            typeof option === "object" && option !== null;

          const optionValue = isObject ? option.value : option;
          const optionLabel = isObject ? option.label : option;
          const optionDisabled = isObject ? !!option.disabled : false;

          return (
            <option
              key={String(optionValue)}
              value={optionValue}
              disabled={optionDisabled}
            >
              {optionLabel}
            </option>
          );
        })}
      </select>

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          right: 12,
          transform: "translateY(-50%)",
          color: resolvedVariant.icon,
          display: "inline-flex",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <ChevronIcon />
      </div>

      {children ? <div style={{ marginTop: 8 }}>{children}</div> : null}
    </div>
  );
}